import { useSearchParams as useWouterSearchParams } from 'wouter-preact';
import { useCallback } from 'preact/hooks';

type UpdaterParams =
  | {
      params?: Record<string, string | number | boolean | null | undefined>;
      keysToRemove?: string | string[];
      partial?: true;
    }
  | {
      params?: Record<string, string | number | boolean | null | undefined>;
      keysToRemove?: never;
      partial: false;
    };

type Options = {
  replace?: boolean;
  state?: any;
};

export const useSearchParams = () => {
  const [searchParams, setSearchParams] = useWouterSearchParams();

  // Update specific search params while preserving others
  // updater.params: params to add/update (null/undefined values will remove)
  // updater.keysToRemove: specific keys to remove before updating (only allowed when partial is true/undefined)
  // updater.partial: partial update (merge with existing) vs replace all params (default: true)
  // options.replace: use history.replaceState instead of history.pushState
  const updateSearchParams = useCallback(
    (updater: UpdaterParams, options?: Options) => {
      // Start with empty params if full replace, otherwise use existing (default: partial/merge)
      const isPartial = updater.partial !== false;
      const newParams = isPartial
        ? new URLSearchParams(searchParams)
        : new URLSearchParams();

      // Remove specified keys (only in partial mode)
      if (updater.keysToRemove && isPartial) {
        const keysArray = Array.isArray(updater.keysToRemove)
          ? updater.keysToRemove
          : [updater.keysToRemove];
        keysArray.forEach((key) => {
          newParams.delete(key);
        });
      }

      // Add/update or remove based on values
      if (updater.params) {
        Object.entries(updater.params).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            newParams.delete(key);
          } else {
            newParams.set(key, String(value));
          }
        });
      }

      setSearchParams(newParams, options);
    },
    [searchParams, setSearchParams],
  );

  // Remove specific search params by keys
  const removeSearchParams = useCallback(
    (keys: string | string[], options?: Options) => {
      const newParams = new URLSearchParams(searchParams);
      const keysToRemove = Array.isArray(keys) ? keys : [keys];

      keysToRemove.forEach((key) => {
        newParams.delete(key);
      });

      setSearchParams(newParams, options);
    },
    [searchParams, setSearchParams],
  );

  return {
    searchParams,
    updateSearchParams,
    removeSearchParams,
  };
};
